// C-05 Booking map (UserFlows C-05, Frontend §6.6): what patients can book, drawn as a tree from the (possibly
// unsaved) draft. Ported from the mockup's `lib/bookingMap.ts`, changed for D-73: there are no work days, so every
// shown provider is listed under every shown location, and the mockup's two "doesn't work at a shown location"
// reasons become one "No location is shown". The server's `options` applies the same rules (API.md §6.10, Backend
// §11.1); T-C05-13 runs one shared fixture table through both.
import type { Settings } from './types'

export type MapKind = 'location' | 'provider' | 'visit-type'
export type MapBy = 'location' | 'visit-type'
export type NodeState = 'ok' | 'red' | 'amber' | 'off'
export type SettingsTab = 'visit-types' | 'locations' | 'providers'

export interface MapNode {
  /** Path of ids, unique in the tree. */
  key: string
  kind: MapKind
  id: string
  label: string
  sub?: string
  state: NodeState
  reason?: string
  tab: SettingsTab
  children: MapNode[]
}

export interface BookingMap {
  roots: MapNode[]
  /** Turned on, but nowhere in the tree. */
  unplaced: MapNode[]
  counts: { locations: number; providers: number; visitTypes: number; red: number; amber: number }
}

export const MAP_REASONS = {
  removed: 'Removed in Practice Fusion',
  location: 'No bookable visits here',
  provider: 'Takes none of the visit types that are on',
  noLocation: 'No location is shown',
  visitType: 'No shown provider can take it',
} as const

const LEVELS: Record<MapBy, MapKind[]> = {
  location: ['location', 'provider', 'visit-type'],
  'visit-type': ['visit-type', 'location', 'provider'],
}
const TAB: Record<MapKind, SettingsTab> = { location: 'locations', provider: 'providers', 'visit-type': 'visit-types' }

/** "Primary Care Center – Downtown" → "Downtown" (the mockup's `shortLocation`). */
export const shortLocation = (name: string): string => name.replace(/^Primary Care Center\s*[–-]\s*/, '')

/** "45 min" or "30 min (default)" (mockup `lengthLabel`). */
export const lengthLabel = (st: Settings, override: number | null): string =>
  override === null ? `${String(st.rules.defaultDurationMinutes)} min (default)` : `${String(override)} min`

interface Item {
  kind: MapKind
  id: string
  name: string
  on: boolean
  removed: boolean
  sub: string
}
type Path = Partial<Record<MapKind, string>>

export function buildBookingMap(st: Settings, by: MapBy, showHidden: boolean): BookingMap {
  const items: Record<MapKind, Item[]> = {
    location: st.locations.map((l) => ({
      kind: 'location',
      id: l.id,
      name: shortLocation(l.name),
      on: l.visible,
      removed: l.removedInPf,
      sub: l.address,
    })),
    provider: st.providers.map((p) => ({
      kind: 'provider',
      id: p.id,
      name: p.credentials ? `${p.name}, ${p.credentials}` : p.name,
      on: p.visible,
      removed: p.removedInPf,
      sub: p.specialty,
    })),
    'visit-type': st.visitTypes.map((v) => ({
      kind: 'visit-type',
      id: v.id,
      name: v.name,
      on: v.enabled,
      removed: v.removedInPf,
      sub: lengthLabel(st, v.durationOverrideMinutes) + (v.telehealth ? ' · Video' : ''),
    })),
  }
  const providerIdsOf = new Map(st.visitTypes.map((v) => [v.id, v.providerIds]))
  const takes = (vtId: string, provId: string) => {
    const ids = providerIdsOf.get(vtId)
    return ids === null || ids === undefined || ids.includes(provId)
  }
  const live = (k: MapKind) => items[k].filter((i) => i.on)
  const pool = (k: MapKind) => items[k].filter((i) => i.on || showHidden)

  // Does this partial path describe something real? D-73: every provider works at every location, so only the
  // visit type's providers matter. Only switched-on providers can link a location to a visit type.
  const fits = (p: Path) => {
    const vt = p['visit-type']
    if (p.provider && vt && !takes(vt, p.provider)) return false
    if (p.location && vt && !p.provider) return live('provider').some((x) => takes(vt, x.id))
    return true
  }

  const levels = LEVELS[by]
  // Under something that's off, everything is out of patients' reach too, so it's shown off as well.
  const build = (depth: number, path: Path, parentKey: string, parentOff: boolean): MapNode[] => {
    const kind = levels[depth]
    if (!kind) return []
    return pool(kind)
      .filter((it) => fits({ ...path, [kind]: it.id }))
      .map((it) => {
        const next = { ...path, [kind]: it.id }
        const key = parentKey ? `${parentKey}/${it.id}` : it.id
        const off = parentOff || !it.on
        const children = depth < levels.length - 1 ? build(depth + 1, next, key, off) : []
        const node: MapNode = { key, kind, id: it.id, label: it.name, state: 'ok', tab: TAB[kind], children }
        if (it.sub) node.sub = it.sub
        if (off) node.state = 'off'
        else if (it.removed) {
          node.state = 'red'
          node.reason = MAP_REASONS.removed
        } else if (depth < levels.length - 1 && !children.some((c) => c.state !== 'off')) {
          node.state = 'amber'
          node.reason = deadEnd(kind)
        }
        return node
      })
  }
  const roots = build(0, {}, '', false)

  // Switched-on items that never made it into the tree: patients can't reach them at all.
  const placed = new Set<string>()
  const walk = (n: MapNode) => {
    if (n.state !== 'off') placed.add(`${n.kind}:${n.id}`)
    n.children.forEach(walk)
  }
  roots.forEach(walk)
  const anyLocationShown = live('location').length > 0
  const unplaced: MapNode[] = levels.slice(1).flatMap((kind) =>
    live(kind)
      .filter((it) => !placed.has(`${kind}:${it.id}`))
      .map((it) => {
        const node: MapNode = {
          key: `unplaced/${it.id}`,
          kind,
          id: it.id,
          label: it.name,
          tab: TAB[kind],
          children: [],
          state: it.removed ? 'red' : 'amber',
          reason: it.removed ? MAP_REASONS.removed : unreachable(kind, anyLocationShown),
        }
        if (it.sub) node.sub = it.sub
        return node
      }),
  )

  // Count each problem item once, however many places it shows up.
  const red = new Set<string>()
  const amber = new Set<string>()
  const tally = (n: MapNode) => {
    if (n.state === 'red') red.add(`${n.kind}:${n.id}`)
    if (n.state === 'amber') amber.add(`${n.kind}:${n.id}`)
    n.children.forEach(tally)
  }
  roots.forEach(tally)
  unplaced.forEach(tally)

  return {
    roots,
    unplaced,
    counts: {
      locations: live('location').length,
      providers: live('provider').length,
      visitTypes: live('visit-type').length,
      red: red.size,
      amber: amber.size,
    },
  }
}

function deadEnd(kind: MapKind): string {
  if (kind === 'location') return MAP_REASONS.location
  if (kind === 'provider') return MAP_REASONS.provider
  return MAP_REASONS.visitType
}

/**
 * Why a switched-on item never shows up (UserFlows C-05's five reasons, D-73). With no work days, a provider or visit
 * type that is on and not removed is unreachable only when no location is shown, or (for a visit type) when no
 * shown provider can take it, or (for a provider) when it takes none of the visit types that are on.
 */
function unreachable(kind: MapKind, anyLocationShown: boolean): string {
  if (kind === 'location') return MAP_REASONS.location
  if (!anyLocationShown) return MAP_REASONS.noLocation
  return kind === 'provider' ? MAP_REASONS.provider : MAP_REASONS.visitType
}

/** Keys of nodes that need attention, in reading order (roots first, then the unplaced list). */
export function issueKeys(m: BookingMap, state: 'red' | 'amber'): string[] {
  const out: string[] = []
  const walk = (n: MapNode) => {
    if (n.state === state) out.push(n.key)
    n.children.forEach(walk)
  }
  m.roots.forEach(walk)
  m.unplaced.forEach(walk)
  return out
}

/**
 * What the map calls bookable (T-C05-13 parity with the server's `options`): every (location, visit type, provider)
 * path of the by-location tree whose three items are all OK (not off, not removed, not a dead end).
 */
export function bookableTriples(st: Settings): [string, string, string][] {
  const out: [string, string, string][] = []
  for (const l of buildBookingMap(st, 'location', false).roots) {
    if (l.state !== 'ok') continue
    for (const p of l.children) {
      if (p.state !== 'ok') continue
      for (const v of p.children) if (v.state === 'ok') out.push([l.id, v.id, p.id])
    }
  }
  return out.sort((a, b) => a.join('|').localeCompare(b.join('|')))
}
